// built-from: /contextual-facilitator-companion v2 impl:contextual-facilitator-companion role:implementation
import { useEffect, useRef, useState } from 'react';

/** What the consultant did that must create the session (spec 100, decision
 * 10). Browsing an activity allocates nothing; the FIRST message — typed text,
 * an attachment on its own, or starting voice — is what opens it. The intent
 * is held here, above the conversation, so it survives the moment the real
 * conversation replaces the not-yet-opened one. */
export type ActivityFirstIntent =
  | { kind: 'text'; text: string; files: File[] }
  | { kind: 'voice' };

/** Opening is explicit. Once open, navigation resumes the compatible activity.
 * Serialised binding requests cannot leave an earlier process as final focus. */
export function useActivityFacilitation({ scopeKey, engagementKey = '', ensureSession }: {
  scopeKey: string;
  engagementKey?: string;
  ensureSession: () => Promise<string | null>;
}) {
  const [intent, setIntent] = useState<{ engagementKey: string; attempt: number }>();
  const [openRequest, setOpenRequest] = useState(0);
  const [firstIntent, setFirstIntent] = useState<ActivityFirstIntent | null>(null);
  const [binding, setBinding] = useState<{ scopeKey: string; sessionId: string; error: string; opening: boolean }>();
  const ensureRef = useRef(ensureSession); ensureRef.current = ensureSession;
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const active = intent?.engagementKey === engagementKey;
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const ensure = ensureRef.current;
    setBinding({ scopeKey, sessionId: '', error: '', opening: true });
    queue.current = queue.current.catch(() => undefined).then(async () => {
      if (cancelled) return;
      try {
        const sessionId = await ensure();
        if (!sessionId) throw new Error('The facilitator could not open. Check the activity setup and try again.');
        if (!cancelled) setBinding({ scopeKey, sessionId, error: '', opening: false });
      } catch (failure) {
        if (!cancelled) setBinding({ scopeKey, sessionId: '', error: (failure as Error).message, opening: false });
      }
    });
    return () => { cancelled = true; };
  }, [active, scopeKey, intent]);
  const current = active && binding?.scopeKey === scopeKey ? binding : undefined;
  const open = () => {
    setOpenRequest(value => value + 1);
    if (!current?.sessionId && !current?.opening) setIntent(previous => ({ engagementKey, attempt: (previous?.attempt || 0) + 1 }));
  };
  return {
    sessionId: current?.sessionId || '', opening: current?.opening || false,
    error: current?.error || '', active, openRequest,
    /** Queued until the bound conversation has actually sent it. A failure
     * keeps it, so retrying does not lose what the consultant said. */
    firstIntent,
    open,
    startWith: (first: ActivityFirstIntent) => { setFirstIntent(first); open(); },
    clearFirstIntent: () => setFirstIntent(null),
    close: () => setIntent(undefined),
  };
}
